import { useCallback, useRef, type DragEvent } from 'react'
import { useReactFlow, type Node } from '@xyflow/react'
import { findGroupAtPoint, absoluteToRelativePositionDeep } from '@/lib/subflow-utils'
import type { NodeKind, NodeOption } from '@/data/nodeOptions'

export function useDnDNodes(setNodes: React.Dispatch<React.SetStateAction<Node[]>>) {
  const rf = useReactFlow()
  const containerRef = useRef<HTMLDivElement>(null)

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      const type = e.dataTransfer.getData('application/reactflow') as NodeKind
      if (!type) return
      const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
      const id = `${type}-${crypto.randomUUID().slice(0, 5)}`

      // Detectar si el drop ocurre dentro de un SubFlow para crear el nodo como hijo
      const currentNodes = rf.getNodes() as Node[]
      const targetGroup = findGroupAtPoint(pos, currentNodes)

      if (type === 'sticky') {
        // REGLA: Sticky notes NO pueden entrar en grupos; siempre como raíz
        setNodes((nds) =>
          nds.concat({
            id,
            type,
            position: pos,
            data: {
              label: 'Note',
              text: '',
              color: 'yellow',
              width: 200,
              height: 150,
              shadow: true,
              fontSize: 12,
            },
          })
        )
        return
      }

      const label = type === 'trigger' ? 'Data Source' : type === 'action' ? 'Asset' : 'Domain'

      // Los SubFlows no se anidan; crear como raíz siempre
      if (type === 'subflow' || !targetGroup) {
        setNodes((nds) =>
          nds.concat(
            type === 'subflow'
              ? { id, type, position: pos, data: { label }, style: { width: 400, height: 300, padding: 8 } }
              : { id, type, position: pos, data: { label } }
          )
        )
        return
      }

      // Crear como hijo del grupo con posición relativa y extent: 'parent'
      const relativePos = absoluteToRelativePositionDeep(pos, targetGroup.id, currentNodes)
      setNodes((nds) =>
        nds.concat({
          id,
          type,
          position: relativePos,
          parentId: targetGroup.id,
          extent: 'parent',
          data: { label },
        } as any)
      )
    },
    [rf, setNodes]
  )

  const addAtCenter = useCallback(
    (type: NodeKind, nodeOption?: NodeOption) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const pos = rf.screenToFlowPosition({ x: centerX, y: centerY })
      const id = `${type}-${crypto.randomUUID().slice(0, 5)}`

      const currentNodes = rf.getNodes() as Node[]
      const targetGroup = findGroupAtPoint(pos, currentNodes)

      if (type === 'sticky') {
        // Sticky como raíz siempre
        setNodes((nds) =>
          nds.concat({
            id,
            type,
            position: pos,
            data: {
              label: 'Note',
              text: '',
              color: 'yellow',
              width: 200,
              height: 150,
              shadow: true,
              fontSize: 12,
            },
          })
        )
        return
      }

      // Use data from NodeOption if available (from workflow nodes API)
      const label = nodeOption?.title || (type === 'trigger' ? 'Data Source' : type === 'action' ? 'Asset' : 'Domain')

      // Build node data from NodeOption
      const nodeData = {
        label,
        icon: nodeOption?.icon,
        vendor: nodeOption?.description,
        ...(nodeOption?.nodeData || {}), // Spread any additional data from the workflow node
      }

      // No anidar subflows o cuando no hay grupo bajo el centro
      if (type === 'subflow' || !targetGroup) {
        setNodes((nds) =>
          nds.concat(
            type === 'subflow'
              ? { id, type, position: pos, data: nodeData, style: { width: 400, height: 300, padding: 8 } }
              : { id, type, position: pos, data: nodeData }
          )
        )
        return
      }

      // Crear como hijo del grupo en el centro actual
      const relativePos = absoluteToRelativePositionDeep(pos, targetGroup.id, currentNodes)
      setNodes((nds) =>
        nds.concat({
          id,
          type,
          position: relativePos,
          parentId: targetGroup.id,
          extent: 'parent',
          data: nodeData,
        } as any)
      )
    },
    [rf, setNodes]
  )

  return { containerRef, onDrop, addAtCenter }
}
